import { cloneDeep, range } from "lodash";
import { Form, getIn } from "formik";
import React, { useState, useEffect } from "react";
import ReactSelect, { components as SelectComponents } from "react-select";
import { useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { allottedTimeFactory } from "@/components/Provider/ScheduleAvailability/constants";
import { Button2 } from "@/ui2/atoms/Button";
import CardSecondary from "@/components/UI/Card/CardSecondary";
import { CustomForm } from "@/component-library";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import DatePickerWithDisplay from "@/components/UI/DatePickerWithDisplay";
import dayjs from "@/lib/helpers/dayjs";
import { EditNoteWrapper } from "@/portals/(shared)/(new-note)/components/EditNoteWrapper";
import FormikInput from "@/components/UI/FormikInput";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import images from "@/utils/images";
import InputError from "@/components/UI/InputError";
import Label from "@/components/UI/Label";
import { patientRequests } from "@/api/(requests)/patient";
import { providerRequests } from "@/api/(requests)/provider";
import { SCREENING_OPTION_LABEL_MAP } from "@/constants/rating";
import { sharedRequests } from "@/api/(requests)/shared";
import { testLocator } from "@/lib/helpers/test-locator";

import { ENCOUNTER_INITIAL_VALUES } from "../constants/encounter";
import { EncounterForm } from "../components/EncounterForm";
import GenericQuestionaire from "../components/GenericQuestionaire";

import {
  CARE_COMMUNICATION_INITIAL_VALUES,
  CARE_COMMUNICATION_SCHEMA,
  CARE_COMMUNICATION_VALIDATION,
} from "./CareCommunicationForm.schema";

export function CareCommunicationNewNote() {
  const { patientId } = useParams();
  const history = useHistory();
  const profile = useSelector((state) => state.auth.profile);

  const [icd10Axis1, setIcd10Axis1] = useState(null);
  const [icd10Axis2, setIcd10Axis2] = useState(null);
  const [initialValues, setInitialValues] = useState(null);
  const [patientName, setPatientName] = useState("");
  const [questionList, setQuestionList] = useState(null);
  const [schema, setSchema] = useState(null);

  useEffect(() => {
    const load = async () => {
      const icd10response = await providerRequests.fetch_ICD10_dataset();
      const urlParams = { patientId };

      const patientDiagnosis = await sharedRequests.get_patient_diagnosis({ urlParams });
      const patientDetails = await sharedRequests.fetch_patient_details({ urlParams });

      setIcd10Axis1(icd10response.filter((icd) => icd.axis === "1"));
      setIcd10Axis2(icd10response.filter((icd) => icd.axis === "2"));
      setPatientName(patientDetails.user.first_name + " " + patientDetails.user.last_name);
      setQuestionList({
        gad: (await patientRequests.fetch_GAD7_questions())["question"],
        phq: (await patientRequests.fetch_PHQ9_questions())["question"],
      });

      const providerType = profile.provider_type;
      const values = cloneDeep(CARE_COMMUNICATION_INITIAL_VALUES);

      values.patient_id = patientId;
      values.patient_first_name = patientDetails.user.first_name;
      values.patient_last_name = patientDetails.user.last_name;
      values.provider_id = profile.username;
      values.provider_type = providerType;
      values.provider_cp_credentials = profile.cp_credentials;
      values.provider_name = profile.name;
      values.session_date = dayjs().format("MM/DD/YYYY");
      values.ICD_10_axis_1_diagnosis = patientDiagnosis.ICD_10_axis_1_diagnosis;
      values.ICD_10_axis_2_diagnosis = patientDiagnosis.ICD_10_axis_2_diagnosis;
      values.content.encounters = [];
      setInitialValues(values);

      const schemaClone = cloneDeep(CARE_COMMUNICATION_SCHEMA);
      const cptCodesFormEl = schemaClone.find((formEl) => formEl.name === "call_type");

      cptCodesFormEl.elementConfig.options = cptCodesFormEl.elementConfig.options.filter((option) =>
        option.provider_types.includes(providerType),
      );
      setSchema(schemaClone);
    };

    load();
  }, [patientId, profile]);

  const addEncounter = async (formikProps) => {
    const updatedEncounters = [...formikProps.values.content.encounters];

    updatedEncounters.push(cloneDeep(ENCOUNTER_INITIAL_VALUES));
    await formikProps.setFieldValue("content.encounters", updatedEncounters);
  };

  const removeEncounter = async (formikProps, encounterIndex) => {
    const updatedEncounters = [...formikProps.values.content.encounters];

    updatedEncounters[encounterIndex].delete_marker = true;
    updatedEncounters[encounterIndex].encounter_type = ["none"];
    updatedEncounters[encounterIndex].setting = ["none"];
    await formikProps.setFieldValue("content.encounters", updatedEncounters);
  };

  const signatureClickHandler = async (name, formikProps) => {
    await formikProps.setFieldValue(
      name,
      formikProps.values.signature ? "" : formikProps.values.provider_name,
    );
  };

  const optionClickHandler = async (name, option, options, formikProps) => {
    const selectedOptions = [...options];

    if (selectedOptions.includes(option.value)) {
      selectedOptions.splice(selectedOptions.indexOf(option.value), 1);
      option.checked = false;
    } else {
      selectedOptions.push(option.value);
      option.checked = true;
    }
    await formikProps.setFieldValue(name, selectedOptions);
  };

  const setFieldValue = async (name, value, formikProps) => {
    await formikProps.setFieldValue(name, value);
  };

  const dateClickHandler = async (dateStr, _eventType, formEl, formikProps) => {
    const [year, month, day] = dateStr.split("-");

    await formikProps.setFieldValue(formEl.name, `${month}/${day}/${year}`);
  };

  const reasonForCommunicationClickHandler = (name, option, formikProps) => {
    const existingReasons = formikProps.values.content.reason_for_communication;

    if (
      option.value === "Acute Care Encounter" &&
      !existingReasons.includes(option.value) &&
      !formikProps.values.content.encounters.filter((encounter) => !encounter.delete_marker).length
    ) {
      addEncounter(formikProps);
    } else if (option.value === "Acute Care Encounter" && existingReasons.includes(option.value)) {
      range(formikProps.values.content.encounters.length).forEach((encounterIndex) =>
        removeEncounter(formikProps, encounterIndex),
      );
    }
    optionClickHandler(name, option, existingReasons, formikProps);
  };

  const preventEnterFormSubmission = (e) => {
    if (e.key === "Enter" || e.code === "Enter") {
      e.preventDefault();
    }
  };

  const renderForm = (formikProps) => (
    <>
      <Form onKeyDown={preventEnterFormSubmission}>
        <div className="row">
          {schema.map((formEl) => {
            if (formEl.name === "content.reason_for_communication") {
              formEl.onclick = (name, option) =>
                reasonForCommunicationClickHandler(name, option, formikProps);
              const openTextOption = formEl.elementConfig.options.find((option) =>
                Object.hasOwn(option, "openText"),
              );

              openTextOption.onChange = async (e) =>
                await formikProps.setFieldValue(
                  "content.reason_for_communication_other",
                  e.target.value,
                );
            }

            const error = getIn(formikProps.errors, formEl.name);
            const touched = getIn(formikProps.touched, formEl.name);

            if (formEl.elementType === "encounters") {
              const isEncounterSelected =
                formikProps.values.content.reason_for_communication.includes(
                  "Acute Care Encounter",
                );

              if (!isEncounterSelected) return null;

              return (
                <React.Fragment key={formEl.name}>
                  <div>
                    {formikProps.values.content.encounters.map((_encounter, encounterIndex) => (
                      <EncounterForm
                        dateClickHandler={(dateStr, eventType, el) =>
                          dateClickHandler(dateStr, eventType, el, formikProps)
                        }
                        encounterIndex={encounterIndex}
                        formikProps={formikProps}
                        key={encounterIndex}
                        optionClickHandler={optionClickHandler}
                        removeEncounter={removeEncounter}
                        setFieldValue={(name, value) => setFieldValue(name, value, formikProps)}
                      />
                    ))}
                    <div className="d-flex mb-2 tw:justify-center">
                      <Button2
                        onPress={() => addEncounter(formikProps)}
                        size="sm"
                        variant="secondary"
                      >
                        Add Encounter
                      </Button2>
                    </div>
                  </div>
                  <div className="border-bottom col-12 my-2" />
                </React.Fragment>
              );
            } else if (formEl.name.includes("ICD_10_axis")) {
              return (
                <div
                  className="col-12 col-lg-6 mb-3"
                  key={formEl.name}
                  {...(formEl.name === "ICD_10_axis_1_diagnosis"
                    ? testLocator("primaryDiagnosisSelect")
                    : {})}
                >
                  <Label>{formEl.elementConfig.label}</Label>
                  <ReactSelect
                    className="basic-multi-select"
                    classNamePrefix="select"
                    components={{
                      ClearIndicator: null,
                      Option: ({ children, innerProps, ...props }) => (
                        <SelectComponents.Option
                          {...props}
                          innerProps={{ ...innerProps, ...testLocator("diagnosisOption") }}
                        >
                          {children}
                        </SelectComponents.Option>
                      ),
                    }}
                    defaultValue={formikProps.values[formEl.name]}
                    inputId={
                      formEl.name === "ICD_10_axis_1_diagnosis"
                        ? "diagnosisSelect1"
                        : "diagnosisSelect2"
                    }
                    isMulti
                    onChange={async (selectedOptions) =>
                      await formikProps.setFieldValue(formEl.name, selectedOptions)
                    }
                    options={formEl.name === "ICD_10_axis_1_diagnosis" ? icd10Axis1 : icd10Axis2}
                  />
                  {error && touched ? (
                    <InputError classes="custom-error" testId="primaryDiagnosisError">
                      {error}
                    </InputError>
                  ) : null}
                </div>
              );
            } else if (formEl.name === "signature") {
              formEl.onclick = (name) => signatureClickHandler(name, formikProps);

              return (
                <div className={formEl.elementClasses} key={formEl.name}>
                  <FormikInput
                    errors={formikProps.errors}
                    formEl={formEl}
                    touched={formikProps.touched}
                  />
                  <div className="w-100 fs-24 border-bottom tw:font-signature">
                    {"X " + formikProps.values.signature}
                  </div>
                </div>
              );
            } else if (formEl.elementType === "datePicker") {
              return (
                <div className={formEl.elementClasses} key={formEl.name}>
                  <DatePickerWithDisplay
                    dateClickHandler={(dateStr, eventType, el) =>
                      dateClickHandler(dateStr, eventType, el, formikProps)
                    }
                    dateStr={formikProps.values[formEl.name]}
                    formEl={formEl}
                    formikProps={formikProps}
                    touched={() => formikProps.setFieldTouched(formEl.name, true)}
                  />
                </div>
              );
            } else {
              return (
                <div className={formEl.elementClasses} key={formEl.name}>
                  <FormikInput
                    disabled={formEl.disabled}
                    errorClasses={formEl.errorClasses}
                    errors={formikProps.errors}
                    formEl={formEl}
                    inputClasses={formEl.inputClasses}
                    touched={formikProps.touched}
                  />
                </div>
              );
            }
          })}
        </div>
        <div className="d-flex justify-content-lg-end tw:justify-center">
          <Button2 size="md" type="submit" {...testLocator("signAndSubmitBtn")}>
            Sign & Submit
          </Button2>
        </div>
      </Form>
    </>
  );

  const submitHandler = async (formData) => {
    if (
      !formData.content.reason_for_communication.includes("Acute Care Encounter") &&
      formData.content.encounters
    ) {
      formData.content.encounters = [];
    } else {
      formData.content.encounters = formData.content.encounters.filter(
        (encounter) => !encounter.delete_marker,
      );
    }

    formData.session_from = dayjs(
      `${formData.session_from_hr}:${formData.session_from_min} ${formData.session_from_am_pm}`,
      "h:mm a",
    ).format("HH:mm");

    formData.session_to = dayjs(formData.session_from, "HH:mm")
      .add(allottedTimeFactory(formData.call_type), "minutes")
      .format("HH:mm");

    formData.is_billable = formData.call_type !== "non_billable";

    const urlParams = { patientId };

    await providerRequests.post_progress_note({ data: formData, urlParams });

    history.push(`/app/patient/${patientId}/profile/v2?selectedTab=Notes`);
  };

  const renderGADPHQCard = (value) => {
    const { cardDescription, cardTitle, questionList: qList } = value;

    if (!qList) return;

    const openButtonRender = (
      <CardSecondary className="p-3 d-flex cursor" key={cardTitle}>
        <div className="col-11">
          <div className="row">
            <b>{cardTitle}</b>
          </div>
          <div className="row txt-gry">{cardDescription}</div>
        </div>
        <div className="col-1 d-flex">
          <img
            alt="note icon"
            className="new-note-chevron align-self-center"
            src={images("./common/right-chevron.svg")}
            style={{ maxHeight: "22px" }}
          />
        </div>
      </CardSecondary>
    );

    return (
      <GenericQuestionaire
        displayAsModal={true}
        key={cardTitle}
        labelMap={SCREENING_OPTION_LABEL_MAP}
        modalOpenButton={openButtonRender}
        {...value}
      />
    );
  };

  const questionaireCards = questionList
    ? [
        {
          cardDescription:
            "Complete the screening with the member, this records a billing event and saves the results to their progres trends",
          cardTitle: "Verbally Administer a Patient Health Questionnaire - 9 (PHQ-9)",
          questionaireInfoDescription:
            "Over the past 2 weeks, how often have you been bothered by any of the following problems?",
          questionaireInfoTitle: "Patient Health Questionnaire - 9",
          questionList: questionList.phq,
          submitFn: async (data) =>
            await patientRequests.submit_PHQ9_questions({
              data: {
                ...data,
                billable_data: {
                  patient_name: patientName,
                  provider_name: profile.name,
                  provider_username: profile.username,
                  screening_type: "phq",
                },
                patient_id: patientId,
              },
            }),
          title: "Clinician Supported PHQ-9",
        },
        {
          cardDescription:
            "Complete the screening with the member, this records a billing event and saves the results to their progres trends",
          cardTitle: "Verbally Administer a General Anxiety Disorder - 7 (GAD-7)",
          questionaireInfoDescription:
            "How often have they been bothered by the following over the past 2 weeks?",
          questionaireInfoTitle: "General Anxiety Disorder - 7",
          questionList: questionList.gad,
          submitFn: async (data) =>
            await patientRequests.submit_GAD7_questions({
              data: {
                ...data,
                billable_data: {
                  patient_name: patientName,
                  provider_name: profile.name,
                  provider_username: profile.username,
                  screening_type: "gad",
                },
                patient_id: patientId,
              },
            }),
          title: "Clinician Supported GAD-7",
        },
      ]
    : [];

  return (
    <EditNoteWrapper patientId={patientId}>
      <DashboardCard2
        className=""
        title="Care Communication Note"
        {...getAutomationDataAttr("CareCommunicationForm__careCommunicationTitle")}
      >
        {initialValues && schema && (
          <CustomForm
            initialValues={initialValues}
            onSubmit={submitHandler}
            render={renderForm}
            validateOnBlur={true}
            validateOnChange={true}
            validationSchema={CARE_COMMUNICATION_VALIDATION}
          />
        )}
      </DashboardCard2>

      {questionaireCards.map(renderGADPHQCard)}
    </EditNoteWrapper>
  );
}
